import { PageTitle } from "@/components/PageTitle";
import {
  BackButton,
  Button,
  LoadingCircle,
  Tag,
  TagVariant,
} from "@industriousoffice/react-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

import {
  Account,
  Invoice as InvoiceType,
  PaymentStatus,
  Location,
  InvoicingSystemTypes,
} from "@/api/opb/opbTypes";
import { opbApi } from "@/api/opb/opbApi";
import { formatCurrency } from "@/utils/string";
import { ErrorMessage } from "@/components/ErrorMessage/ErrorMessage";
import Layout from "@/components/Layout";
import { getFullPath } from "@/router/utils/getFullPath";
import { slugs } from "@/router/slugs";
import PaymentStatusTag from "@/components/PaymentStatusTag";
import { useDateFormatter } from "@/hooks/useDateFormatter";

import { InvoiceItemsTable } from "./components/InvoiceItemsTable";

export function Invoice() {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation("invoice");
  const { format, formatDateFromYYYYMMDD } = useDateFormatter();

  const { invoiceId, accountId } = useParams();

  const [invoice, setInvoice] = useState<InvoiceType | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [location, setLocation] = useState<Location | null>(null);

  const [isLoadingInvoice, setIsLoadingInvoice] = useState(true);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isLoading = isLoadingInvoice || isLoadingAccount || isLoadingLocation;

  const isStripeBilling =
    account?.invoicingSystemType === InvoicingSystemTypes.StripeBilling;

  const downloadPDF = async () => {
    if (invoice?.id) {
      setIsLoadingPDF(true);
      const data = await opbApi.fetchInvoicePDF(invoice?.id);
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setIsLoadingPDF(false);
    }
  };

  useEffect(() => {
    if (accountId) {
      opbApi
        .fetchAccount(accountId)
        .then(setAccount)
        .catch((e) => {
          setError(new Error("Error fetching account", { cause: e }));
        })
        .finally(() => {
          setIsLoadingAccount(false);
        });
    }
  }, [accountId]);

  useEffect(() => {
    if (invoiceId && account) {
      opbApi
        .fetchInvoice({
          invoiceId,
          isStripeBilling:
            account.invoicingSystemType === InvoicingSystemTypes.StripeBilling,
        })
        .then(setInvoice)
        .catch((e) => {
          setError(new Error("Error fetching invoice", { cause: e }));
        })
        .finally(() => {
          setIsLoadingInvoice(false);
        });
    }
  }, [invoiceId, account]);

  useEffect(() => {
    if (account?.locationId) {
      setIsLoadingLocation(true);
      opbApi
        .fetchLocation(account.locationId)
        .then(setLocation)
        .catch((e) => {
          setError(new Error("Error fetching location", { cause: e }));
        })
        .finally(() => {
          setIsLoadingLocation(false);
        });
    }
  }, [account]);

  return (
    <Layout>
      <div className="text-base sm:mb-16">
        <BackButton data-testid="invoice-back-btn" onClick={() => navigate(-1)}>
          {t("Back")}
        </BackButton>
        {isLoading && (
          <div className="mt-12">
            <LoadingCircle spinnerSize={30} spinnerColor="#013e3f" />
          </div>
        )}
        <ErrorMessage error={error} className="my-6!" />
        {!isLoading && (
          <div>
            <div className="grid grid-cols-4 mb-4">
              <PageTitle className="col-span-4 ">
                {t("Invoice")} {location?.name && `: ${location.name}`}
              </PageTitle>
              <div
                className="col-span-2 pt-4 lg:pt-1"
                data-testid="invoice-invoice_number-txt"
              >
                {invoice ? (
                  <PaymentStatusTag invoice={invoice} className="md:inline" />
                ) : null}
                <span className="md:ml-2">{invoice?.invoiceNumber || ""}</span>
              </div>
              <div className="flex col-span-2 pt-4 justify-items-end">
                <Button
                  disabled={isLoadingPDF || !invoice}
                  onClick={downloadPDF}
                  className="ml-auto py-4 bg-transparent! text-teal-30! mr-2 border-transparent! hover:bg-grey-95! hover:!color-teal-30 whitespace-nowrap"
                  data-testid="invoice-download_pdf-btn"
                >
                  {isLoadingPDF ? (
                    <LoadingCircle
                      spinnerColor="#ddd"
                      spinnerSize={20}
                      className="mr-2"
                    />
                  ) : (
                    <ArrowDownTrayIcon className="w-5 mr-2" />
                  )}
                  {t("Download PDF")}
                </Button>
                {invoice?.paymentStatus !== PaymentStatus.Paid &&
                invoice?.paymentStatus !== PaymentStatus.Voided ? (
                  <Link
                    to={getFullPath(slugs.payInvoices, { accountId })}
                    state={{ initialInvoiceIds: [invoiceId] }}
                  >
                    <Button
                      data-testid="invoice-pay_invoice-btn"
                      disabled={isLoading}
                      className="hidden py-4 lg:flex"
                    >
                      {t("Pay Invoice")}
                    </Button>
                  </Link>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-8 lg:grid-rows-1">
              <div className="col-span-4 pb-6 lg:col-span-1">
                <div className="text-sm text-grey-30">{t("Invoice Date")}</div>
                <span className="text-base">
                  {invoice?.invoiceDate
                    ? formatDateFromYYYYMMDD(invoice.invoiceDate, "PP")
                    : "?"}
                </span>
              </div>
              <div className="col-span-4 pb-6 lg:col-span-1">
                <div className="text-sm text-grey-30">{t("Due Date")}</div>
                <span className="text-base">
                  {invoice?.dueDate
                    ? formatDateFromYYYYMMDD(invoice.dueDate, "PP")
                    : "?"}
                </span>
              </div>
              <div className="col-span-8 pb-6 lg:col-span-4">
                <Tag
                  variant={
                    account?.autopay ? TagVariant.Positive : TagVariant.Warning
                  }
                  text={account?.autopay ? t("Auto-Pay On") : t("Auto-Pay Off")}
                />
                <div className="text-sm" style={{ paddingTop: 3 }}>
                  {account?.autopay
                    ? t(
                        "As of {{date}}, this invoice is scheduled for auto-pay.",
                        {
                          date: format(new Date(), "PP"),
                        }
                      )
                    : t(
                        "As of {{date}}, this invoice is not scheduled for auto-pay.",
                        {
                          date: format(new Date(), "PP"),
                        }
                      )}
                  <Link to={getFullPath(slugs.paymentMethods, { accountId })}>
                    <span className="ml-2 font-bold text-ocean-60">
                      {t("Manage Auto-Pay")}
                    </span>
                  </Link>
                </div>
              </div>
            </div>

            <div className="w-full border rounded-sm border-grey-60">
              <InvoiceItemsTable
                items={invoice?.items || []}
                currencyCode={account?.currencyCode || "USD"}
                className="hidden lg:table"
                isMobile={false}
              />
              <InvoiceItemsTable
                items={invoice?.items || []}
                currencyCode={account?.currencyCode || "USD"}
                className="lg:hidden"
                isMobile
              />
              <div className="p-2 text-right">
                {isStripeBilling && (
                  <div
                    className="w-full"
                    data-testid="invoice-invoice_sub_total-txt"
                  >
                    <span className="px-1 text-base text-grey-30">
                      {t("Invoice Subtotal")}
                    </span>
                    <span className="inline-block px-1 pl-3 text-2xl font-bold">
                      {formatCurrency({
                        value: invoice?.amountWithoutTax || 0,
                        currency: account?.currencyCode || "USD",
                        lang: language,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                )}

                <div className="w-full" data-testid="invoice-invoice_total-txt">
                  <span className="px-1 text-base text-grey-30">
                    {t("Invoice Total")}
                  </span>
                  <span className="inline-block px-1 pl-3 text-2xl font-bold">
                    {formatCurrency({
                      value: invoice?.amount || 0,
                      currency: account?.currencyCode || "USD",
                      lang: language,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                {!!invoice?.creditMemoAmount && (
                  <div
                    className="w-full"
                    data-testid="invoice-invoice_applied-credit-txt"
                  >
                    <span className="px-1 text-base text-grey-30">
                      {t("Applied Credit")}
                    </span>
                    <span className="inline-block px-1 pl-3 text-2xl font-bold">
                      (
                      {formatCurrency({
                        value: invoice.creditMemoAmount,
                        currency: account?.currencyCode || "USD",
                        lang: language,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      )
                    </span>
                  </div>
                )}
                <div className="w-full" data-testid="invoice-balance_due-txt">
                  <span className="px-1 text-base text-grey-30">
                    {t("Balance due")}
                  </span>
                  <span className="inline-block px-1 pl-3 text-2xl font-bold">
                    {formatCurrency({
                      value: invoice?.balance || 0,
                      currency: account?.currencyCode || "USD",
                      lang: language,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-6 mt-6 lg:grid lg:grid-cols-3 lg:grid-rows-1">
              <div className="lg:row-span-2 lg:mb-6">
                <div className="text-lg font-bold">{t("Bill To")}</div>
                <div>
                  <span className="text-base font-bold">
                    {account?.billToContact?.firstName}{" "}
                    {account?.billToContact?.lastName}
                  </span>
                </div>
                <div>{account?.billToContact?.email}</div>
              </div>
              <div className="mt-2 mb-6 lg:row-span-2 lg:m-0">
                <div className="text-sm text-grey-30">
                  {t("Account Number")}
                </div>
                <div className="text-base">{account?.accountNumber}</div>
                <div className="pt-2 text-sm text-grey-30">
                  {t("Account Balance")}
                </div>
                <div>{account?.balance}</div>
              </div>
              <div className="col-span-2 lg:col-span-1">
                <div className="text-lg font-bold">{t("Bill From")}</div>
                <div className="font-bold">{location?.licensor}</div>
                <div className="text-grey-30">{location?.address}</div>
                <div className="text-grey-30">{location?.address2}</div>
                <div className="text-grey-30">
                  {location?.city} {location?.zipCode}
                </div>
              </div>
            </div>
            <div className="mt-12 lg:pt-12">
              <div className="text-lg font-bold">
                {t("Check payment instructions")}
              </div>
              <div className="text-grey-30">
                {t("If paying via check", {
                  accountNumber: account?.accountNumber,
                  locationId: location?.locationCode,
                  recipient: location?.licensor?.toUpperCase(),
                })}

                <ul className="list-disc list-inside">
                  <li>{t("USPS")}</li>
                  <li>{t("courier")}</li>
                </ul>
                {t("MAC")}
              </div>
            </div>

            <div className="py-12 mt-12 text-sm border-t text-grey-30">
              {t("Payment is due on the invoice due date")} <br />
              <br />
              {t("To the extent applicable")}{" "}
              <a
                href="https://www.industriousoffice.com/portalterms"
                target="_blank"
                rel="noreferrer"
              >
                https://www.industriousoffice.com/portalterms
              </a>
              {" & "}
              <a
                href="https://www.industriousoffice.com/privacypolicy"
                target="_blank"
                rel="noreferrer"
              >
                https://www.industriousoffice.com/privacypolicy
              </a>
              {t("incorporated")}
              <br />
              <br />
              {t(
                "Please contact billing@industriousoffice.com with any questions regarding your invoice."
              )}
            </div>
          </div>
        )}
        <div className="fixed bottom-0 left-0 w-full px-4 py-8 bg-white md:px-16 sm:px-12">
          <Link
            to={getFullPath(slugs.payInvoices, { accountId })}
            state={{ initialInvoiceIds: [invoiceId] }}
          >
            <Button
              data-testid="invoice-pay_invoice_mobile-btn"
              disabled={isLoading}
              className="w-full lg:hidden"
            >
              {t("Pay Invoice")}
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
